import { toast } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/Dialog";
import { DialogContent } from "@/components/ui/Dialog";
import { useForm } from "react-hook-form";
import {
  type AOEAnswerCoding,
  AOEQuestionAnswerType,
  type AOEQuestionnaire,
} from "../../network/useRetrieveAOEForOrder";
import { useSaveAOE } from "../../network/useSaveAOE";
import { TestQuestionsForm } from "./TestQuestionsForm";
import type { AOEFormSchema } from "./schemas";

export const AOEDialog = ({
  close,
  patientId,
  encounterId,
  requestGroupId,
  questionnaires,
}: {
  close: () => void;
  patientId: string;
  encounterId: string;
  requestGroupId: string;
  questionnaires: AOEQuestionnaire[];
}) => {
  const transformQuestionnaires = (questionnaires: AOEQuestionnaire[]) => {
    const questionnairesFormData = { questionnaires: {} } as AOEFormSchema;
    for (const questionnaire of questionnaires) {
      const questionnaireResponseId = questionnaire.questionnaireResponseId;

      const answerValuesByLinkId = {} as Record<
        string,
        string | AOEAnswerCoding | (string | AOEAnswerCoding)[] | undefined
      >;

      for (const answer of questionnaire.answers) {
        if (!answer.answerValues || answer.answerValues.length === 0) {
          continue;
        }
        if (answer.answerValues.length === 1) {
          answerValuesByLinkId[answer.linkId] =
            answer.answerValues[0].answerValue;
        } else {
          answerValuesByLinkId[answer.linkId] = answer.answerValues.map(
            (answerValue) => answerValue.answerValue,
          );
        }
      }

      const questionnaireFormData = {} as Record<
        string,
        string | AOEAnswerCoding | (string | AOEAnswerCoding)[] | undefined
      >;

      for (const question of questionnaire.questions) {
        questionnaireFormData[question.linkId] =
          answerValuesByLinkId[question.linkId] ?? undefined;
      }
      questionnairesFormData.questionnaires[questionnaireResponseId] =
        questionnaireFormData;
    }

    return questionnairesFormData;
  };

  const form = useForm<AOEFormSchema>({
    defaultValues: transformQuestionnaires(questionnaires),
  });

  const expectedAnswerTypes = questionnaires.reduce(
    (acc, questionnaire) => {
      acc[questionnaire.questionnaireResponseId] =
        questionnaire.questions.reduce(
          (acc2, question) => {
            acc2[question.linkId] = question.answerType;
            return acc2;
          },
          {} as Record<string, AOEQuestionAnswerType>,
        );
      return acc;
    },
    {} as Record<string, Record<string, AOEQuestionAnswerType>>,
  );

  const saveAOE = useSaveAOE({
    patientId: patientId,
    encounterId: encounterId,
    requestGroupId: requestGroupId,
  });

  const onSubmit = async (formData: AOEFormSchema) => {
    for (const [
      questionnaireResponseId,
      questionnaireAnswers,
    ] of Object.entries(formData.questionnaires)) {
      const answers = Object.entries(questionnaireAnswers).map(
        ([linkId, answer]) => {
          // For single answer questions which are just a freetext string (valueString)
          if (typeof answer === "string" && answer.trim() !== "") {
            const answerType =
              expectedAnswerTypes[questionnaireResponseId][linkId];
            return {
              linkId,
              answerValues: [
                {
                  answerType:
                    answerType === AOEQuestionAnswerType.TEXT_OR_CODING
                      ? AOEQuestionAnswerType.TEXT
                      : answerType,
                  answerValue: answer,
                },
              ],
            };
          }
          // For multi-select questions (valueCoding[] or string[] or a mix of both)
          if (Array.isArray(answer)) {
            return {
              linkId,
              answerValues: answer.map((a) => {
                if (typeof a === "string") {
                  const answerType =
                    expectedAnswerTypes[questionnaireResponseId][linkId];
                  return {
                    answerType:
                      answerType === AOEQuestionAnswerType.TEXT_OR_CODING
                        ? AOEQuestionAnswerType.TEXT
                        : answerType,
                    answerValue: a,
                  };
                }
                if (typeof a === "object" && a !== null && "code" in a) {
                  const answerType =
                    expectedAnswerTypes[questionnaireResponseId][linkId];
                  return {
                    answerType:
                      answerType === AOEQuestionAnswerType.TEXT_OR_CODING
                        ? AOEQuestionAnswerType.CODING
                        : answerType,
                    answerValue: a,
                  };
                }
                return {
                  answerType:
                    expectedAnswerTypes[questionnaireResponseId][linkId],
                  answerValue: a,
                };
              }),
            };
          }
          // For single answer questions which are valueCodings
          if (
            typeof answer === "object" &&
            answer !== null &&
            "code" in answer &&
            "display" in answer
          ) {
            const answerType =
              expectedAnswerTypes[questionnaireResponseId][linkId];
            return {
              linkId,
              answerValues: [
                {
                  answerType:
                    answerType === AOEQuestionAnswerType.TEXT_OR_CODING
                      ? AOEQuestionAnswerType.CODING
                      : answerType,
                  answerValue: answer,
                },
              ],
            };
          }
          return undefined;
        },
      );
      const filteredAnswers = answers.filter(
        (response) => response !== undefined,
      );
      try {
        await saveAOE.mutateAsync({
          questionnaireResponseId: questionnaireResponseId,
          answers: filteredAnswers,
        });
      } catch (error) {
        toast.error("Failed to save responses");
      }
    }
    close();
  };

  return (
    <Dialog open onOpenChange={() => close()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-full max-w-3xl h-2/3 top-1/2 flex flex-col">
          <DialogHeader>
            <DialogTitle>Order Questions</DialogTitle>
            <DialogDescription className="hidden">aoe modal</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-1 flex-col h-full overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto">
              {questionnaires.map((questionnaire) => {
                if (!questionnaire) return null;

                return (
                  <TestQuestionsForm
                    key={questionnaire.questionnaireResponseId}
                    questionnaire={questionnaire}
                    control={form.control}
                    disabled={saveAOE.isPending}
                  />
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="secondary"
                size="xs"
                onClick={close}
                disabled={saveAOE.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="xs"
                type="submit"
                isLoading={saveAOE.isPending}
              >
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
